import { useMemo, useState } from 'react';
import { useApp } from '../App.jsx';
import { Card, ConfirmDialog, Dialog, Field, Icon, Money, MoneyField, MonthSelect, PersonDot, Seg, useMoneyText } from './ui.jsx';
import { BalanceChart } from './Charts.jsx';
import { monthLabel, dateLabel, addMonths, isDate } from '../lib/calendar.js';
import { fmt, sum, r2 } from '../lib/money.js';
import { setOpening, addTransaction, updateTransaction, deleteTransaction, addGoal, updateGoal, deleteGoal } from '../lib/actions.js';

function TransactionDialog({ tx, onClose }) {
  const { commit, today } = useApp();
  const [dir, setDir] = useState(tx ? (tx.amount < 0 ? 'out' : 'in') : 'out');
  const amount = useMoneyText(tx ? Math.abs(tx.amount) : null, { allowNegative: false });
  const [date, setDate] = useState(tx?.date || today);
  const [label, setLabel] = useState(tx?.label || '');
  const [error, setError] = useState(null);
  const submit = () => {
    if (!amount.parsed.ok || !amount.parsed.value) { setError('Enter an amount'); return; }
    if (!isDate(date)) { setError('Pick a date'); return; }
    const value = dir === 'out' ? -amount.parsed.value : amount.parsed.value;
    const res = tx
      ? commit(`Edit savings entry ${label}`, (s) => updateTransaction(s, tx.id, { date, amount: value, label: label.trim() || tx.label }))
      : commit(`${dir === 'out' ? 'Took' : 'Put'} ${fmt(amount.parsed.value)} ${dir === 'out' ? 'out of' : 'into'} savings`, (s) => addTransaction(s, { date, amount: value, label }), { undoToast: true });
    if (res.error) setError(res.error); else onClose();
  };
  return (
    <Dialog title={tx ? 'Edit entry' : 'Money in or out of savings'} onClose={onClose} footer={<><span className="spacer" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn primary" onClick={submit}>Save</button></>}>
      <p className="small muted">For things outside the monthly routine: buying something with savings, a gift of money, interest, moving money between accounts.</p>
      {error && <div className="banner danger" role="alert">{error}</div>}
      <Seg ariaLabel="Direction" value={dir} onChange={setDir} options={[{ value: 'out', label: 'Taken out' }, { value: 'in', label: 'Put in' }]} />
      <div className="grid2">
        <MoneyField label="Amount" m={amount} autoFocus />
        <Field label="Date"><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      </div>
      <Field label="What for"><input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={dir === 'out' ? 'e.g. New sofa' : 'e.g. Interest'} maxLength={120} /></Field>
    </Dialog>
  );
}

function GoalDialog({ goal, onClose }) {
  const { commit } = useApp();
  const [name, setName] = useState(goal?.name || '');
  const target = useMoneyText(goal?.target ?? null, { allowNegative: false });
  const [by, setBy] = useState(goal?.by || null);
  const [error, setError] = useState(null);
  const submit = () => {
    if (!target.parsed.ok || !target.parsed.value) { setError('Enter a target amount'); return; }
    const res = goal
      ? commit(`Edit goal ${name}`, (s) => updateGoal(s, goal.id, { name, target: target.parsed.value, by }))
      : commit(`Add goal ${name}`, (s) => addGoal(s, { name, target: target.parsed.value, by }));
    if (res.error) setError(res.error); else onClose();
  };
  return (
    <Dialog title={goal ? 'Edit goal' : 'New goal'} onClose={onClose} footer={<><span className="spacer" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn primary" onClick={submit}>Save</button></>}>
      {error && <div className="banner danger" role="alert">{error}</div>}
      <Field label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Emergency fund" maxLength={60} /></Field>
      <div className="grid2">
        <MoneyField label="Target" m={target} />
        <Field label="By (optional)" group><MonthSelect ariaLabel="By (optional)" value={by} onChange={setBy} allowEmpty emptyLabel="No date" /></Field>
      </div>
    </Dialog>
  );
}

export default function Savings() {
  const { data, model, commit } = useApp();
  const [txEdit, setTxEdit] = useState(null);
  const [goalEdit, setGoalEdit] = useState(null);
  const [delTx, setDelTx] = useState(null);
  const [delGoal, setDelGoal] = useState(null);
  const opening = useMoneyText(data.savings.opening);
  const openingChanged = opening.parsed.ok && opening.parsed.value !== null && opening.parsed.value !== data.savings.opening;

  const points = useMemo(() => model.months.map((m) => ({ key: m.key, label: monthLabel(m.key), short: monthLabel(m.key, { short: true }).replace(' 20', " '"), value: m.balance, projected: m.key > model.current })), [model]);
  const in12 = model.byKey[addMonths(model.current, 12)]?.projectedBalance;
  const last = model.months[model.months.length - 1];
  const done = model.months.filter((m) => m.key <= model.current);
  const recent = done.slice(-6);
  const avg = recent.length ? r2(sum(recent.map((m) => m.savings)) / recent.length) : 0;

  const perPerson = data.people.map((p) => {
    const entries = model.months.flatMap((m) => m.people.filter((e) => e.personId === p.id && (e.due || e.confirmed)));
    const income = sum(entries.map((e) => e.actual.income));
    const saved = sum(entries.map((e) => e.actual.contribution));
    return { p, income, saved, rate: income > 0 ? saved / income : 0, months: entries.length };
  });

  const reachBy = (target) => {
    const hit = model.months.find((m) => m.projectedBalance >= target);
    return hit ? hit.key : null;
  };

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div><h1>Savings</h1><p>Everything left after expenses and spending money, month after month.</p></div>
        <span className="spacer" />
        <button type="button" className="btn primary" onClick={() => setTxEdit('new')}><Icon name="plus" />Money in or out</button>
      </div>
      <div className="grid3">
        <div className="tile hero"><div className="label">Savings now</div><div className="value num">{fmt(model.balance)}</div><div className="sub">{model.pending.length ? `${model.pending.length} paydays still to review` : 'everything reviewed'}</div></div>
        <div className="tile"><div className="label">In 12 months (projected)</div><div className="value num">{in12 !== undefined ? fmt(in12) : '—'}</div><div className="sub">if things go as expected</div></div>
        <div className="tile"><div className="label">Average per month</div><div className="value num">{fmt(avg)}</div><div className="sub">last {recent.length} months</div></div>
        {last && <div className="tile"><div className="label">By {monthLabel(last.key)}</div><div className="value num">{fmt(last.projectedBalance)}</div><div className="sub">projected</div></div>}
      </div>

      <Card title="Balance over time"><BalanceChart points={points} height={240} /></Card>

      <div className="grid2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Card title="Goals" actions={<button type="button" className="btn small" onClick={() => setGoalEdit('new')}><Icon name="plus" />Goal</button>}>
          {!data.savings.goals.length ? <p className="muted">No goals yet. A common first one: an emergency fund of 3–6 months of expenses.</p> : data.savings.goals.map((g) => {
            const pctDone = Math.max(0, Math.min(1, model.balance / g.target));
            const when = reachBy(g.target);
            const late = g.by && when && when > g.by;
            return (
              <div key={g.id} className="list-row" style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }} className="stack tight">
                  <div className="row"><b>{g.name}</b><span className="spacer" /><span className="num">{fmt(Math.min(model.balance, g.target))} / {fmt(g.target)}</span></div>
                  <div className="progress"><div style={{ width: `${pctDone * 100}%` }} /></div>
                  <div className="small muted">{pctDone >= 1 ? 'Reached' : when ? `Projected for ${monthLabel(when)}` : 'Not reached in the projected months'}{g.by ? ` · target ${monthLabel(g.by)}` : ''}{late && <span style={{ color: 'var(--warn)' }}> · later than planned</span>}</div>
                </div>
                <button type="button" className="btn ghost icon small" aria-label={`Edit ${g.name}`} onClick={() => setGoalEdit(g)}><Icon name="edit" /></button>
                <button type="button" className="btn ghost icon small" aria-label={`Delete ${g.name}`} onClick={() => setDelGoal(g)}><Icon name="trash" /></button>
              </div>
            );
          })}
        </Card>
        <Card title="Who saved what">
          <table className="plain">
            <thead><tr><th>Person</th><th>Brought in</th><th>Saved</th><th>Rate</th></tr></thead>
            <tbody>
              {perPerson.map(({ p, income, saved, rate }) => (
                <tr key={p.id}><td><span className="pill-person"><PersonDot color={p.color} />{p.name}</span></td><td><Money value={income} /></td><td><Money value={saved} /></td><td className="num">{Math.round(rate * 100)}%</td></tr>
              ))}
            </tbody>
          </table>
          <p className="small muted" style={{ marginTop: 8 }}>Paydays so far. Expenses that came in over budget and money moved in/out aren't per person.</p>
        </Card>
      </div>

      <Card title="Starting balance">
        <div className="row wrap" style={{ alignItems: 'flex-end' }}>
          <div style={{ width: 220 }}><MoneyField label={`Savings you had before ${monthLabel(data.settings.startMonth)}`} m={opening} /></div>
          <button type="button" className="btn" disabled={!openingChanged} onClick={() => commit(`Starting balance ${fmt(opening.parsed.value)}`, (s) => setOpening(s, opening.parsed.value), { undoToast: true })}>Save</button>
        </div>
      </Card>

      <Card title="Money moved in or out">
        {!data.savings.transactions.length ? <p className="muted">Nothing yet.</p> : (
          <table className="plain">
            <thead><tr><th>Date</th><th className="left">What</th><th>Amount</th><th /></tr></thead>
            <tbody>
              {[...data.savings.transactions].reverse().map((t) => (
                <tr key={t.id} className={t.date > model.today ? 'muted' : ''}>
                  <td>{dateLabel(t.date, { weekday: false })}{t.date > model.today ? ' (planned)' : ''}</td>
                  <td className="left">{t.label}</td>
                  <td><Money value={t.amount} sign color /></td>
                  <td>
                    <button type="button" className="btn ghost icon small" aria-label={`Edit ${t.label}`} onClick={() => setTxEdit(t)}><Icon name="edit" /></button>
                    <button type="button" className="btn ghost icon small" aria-label={`Delete ${t.label}`} onClick={() => setDelTx(t)}><Icon name="trash" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {txEdit && <TransactionDialog tx={txEdit === 'new' ? null : txEdit} onClose={() => setTxEdit(null)} />}
      {goalEdit && <GoalDialog goal={goalEdit === 'new' ? null : goalEdit} onClose={() => setGoalEdit(null)} />}
      {delTx && <ConfirmDialog title="Delete this entry?" message={`${delTx.label}: ${fmt(delTx.amount, { sign: true })} on ${delTx.date}`} danger confirmLabel="Delete" onCancel={() => setDelTx(null)} onConfirm={() => { commit(`Delete savings entry ${delTx.label}`, (s) => deleteTransaction(s, delTx.id), { undoToast: true }); setDelTx(null); }} />}
      {delGoal && <ConfirmDialog title={`Delete the goal ${delGoal.name}?`} message="The savings themselves aren't touched." danger confirmLabel="Delete" onCancel={() => setDelGoal(null)} onConfirm={() => { commit(`Delete goal ${delGoal.name}`, (s) => deleteGoal(s, delGoal.id), { undoToast: true }); setDelGoal(null); }} />}
    </div>
  );
}
